<template>
  <v-app>
    <!-- Dealer Header with Sidebar -->
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      @navigate="handleNavigation"
    />

    <!-- Main Content -->
    <v-main class="auth-pages dealer-main-content">
      <v-container fluid class="dashboard-container">
        <!-- Welcome Section -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="welcome-card" color="primary" dark>
              <v-card-text class="pa-6">
                <v-row align="center">
                  <v-col cols="12" md="8">
                    <h1 class="display-1 font-weight-light mb-2">
                      Welcome back, {{ userName }}!
                    </h1>
                    <p class="headline font-weight-light">
                      {{ businessName }} Dashboard
                    </p>
                    <p class="body-1 mb-0">
                      Manage your dealership operations and connect with referral agents.
                    </p>
                  </v-col>
                  <v-col cols="12" md="4" class="text-center">
                    <v-icon size="120" class="welcome-icon material-icons-round">business</v-icon>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Status Alert -->
        <v-row v-if="dealerStatus === '0'" class="mb-6">
          <v-col cols="12">
            <v-alert
              type="warning"
              prominent
              border="left"
              colored-border
            >
              <v-row align="center">
                <v-col class="grow">
                  <div class="title">Account Pending Approval</div>
                  <div>Your dealer account is currently under review. You'll receive full access once approved by our admin team.</div>
                </v-col>
                <v-col class="shrink">
                  <v-btn color="warning" outlined>
                    Contact Support
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Top Stats Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <v-card-text class="text-center pa-4">
                <v-icon size="48" color="primary" class="mb-2 material-icons-round">inventory</v-icon>
                <div class="display-1 font-weight-bold primary--text">{{ dashboardData.stats.live_vehicles }}</div>
                <div class="body-2 grey--text">Live Vehicles</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <v-card-text class="text-center pa-4">
                <v-icon size="48" color="success" class="mb-2 material-icons-round">people</v-icon>
                <div class="display-1 font-weight-bold success--text">{{ dashboardData.stats.active_agents }}</div>
                <div class="body-2 grey--text">Active Agents</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <v-card-text class="text-center pa-4">
                <v-icon size="48" color="info" class="mb-2 material-icons-round">trending_up</v-icon>
                <div class="display-1 font-weight-bold info--text">{{ dashboardData.stats.pending_leads }}</div>
                <div class="body-2 grey--text">Pending Leads</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card">
              <v-card-text class="text-center pa-4">
                <v-icon size="48" color="warning" class="mb-2 material-icons-round">account_balance_wallet</v-icon>
                <div class="display-1 font-weight-bold warning--text">${{ dashboardData.stats.unpaid_commissions }}</div>
                <div class="body-2 grey--text">Unpaid Commissions</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- New Dashboard Sections from Image -->
        <v-row class="mb-6">
          <!-- Left Column -->
          <v-col cols="12" md="6">
            <!-- Inventory Status -->
            <v-card class="mb-4">
              <v-card-title>
                <span class="material-icons-round mr-2">inventory</span>
                Inventory Status
              </v-card-title>
              <v-card-text>
                <div v-if="loading" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-2">Loading inventory data...</div>
                </div>
                <div v-else>
                  <div class="mb-2">
                    <span class="body-1">• {{ inventoryStats.live_vehicles }} vehicles live</span>
                  </div>
                  <div class="mb-2">
                    <span class="body-1">• {{ inventoryStats.pending_approval }} pending approval</span>
                  </div>
                  <div class="mb-3">
                    <span class="body-1">• Last sync: {{ inventoryStats.last_sync }}</span>
                  </div>
                  <div>
                    <v-btn
                      small
                      color="primary"
                      outlined
                      @click="manageInventory"
                      class="text-transform-none"
                    >
                      Manage Inventory
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Commission Overview -->
            <v-card>
              <v-card-title>
                <span class="material-icons-round mr-2">account_balance_wallet</span>
                Commission Overview
              </v-card-title>
              <v-card-text>
                <div v-if="loading" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-2">Loading commission data...</div>
                </div>
                <div v-else>
                  <div class="mb-2">
                    <span class="body-1">• Flat: ${{ commissionOverview.flat_rate }} per sale</span>
                  </div>
                  <div class="mb-3">
                    <span class="body-1">• Tiered: {{ commissionOverview.tiered_rate }}% on ${{ commissionOverview.tiered_threshold }}k+ vehicles</span>
                  </div>
                  <div>
                    <v-btn
                      small
                      color="primary"
                      outlined
                      @click="editCommissions"
                      class="text-transform-none"
                    >
                      Edit Commissions
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column -->
          <v-col cols="12" md="6">
            <!-- Agent Performance -->
            <v-card class="mb-4">
              <v-card-title>
                <span class="material-icons-round mr-2">people</span>
                Agent Performance
              </v-card-title>
              <v-card-text>
                <div v-if="loading" class="text-center py-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-2">Loading agent data...</div>
                </div>
                <div v-else>
                  <div class="mb-2">
                    <span class="body-1">• Top Agent: {{ agentPerformanceData.top_agent_name }} ({{ agentPerformanceData.top_agent_leads }} leads)</span>
                  </div>
                  <div class="mb-2">
                    <span class="body-1">• Avg Response: {{ agentPerformanceData.avg_response_time }} hrs</span>
                  </div>
                  <div class="mb-3">
                    <span class="body-1">• Active Agents: {{ agentPerformanceData.active_agents }}</span>
                  </div>
                  <div>
                    <v-btn
                      small
                      color="primary"
                      outlined
                      @click="viewAgents"
                      class="text-transform-none"
                    >
                      View Agents
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Recent Reviews -->
            <v-card>
              <v-card-title>
                <span class="material-icons-round mr-2">star</span>
                Recent Reviews
              </v-card-title>
              <v-card-text>
                <div v-if="dashboardData.recent_reviews && dashboardData.recent_reviews.length > 0">
                  <div v-for="review in dashboardData.recent_reviews.slice(0, 3)" :key="review.id" class="mb-3">
                    <div class="d-flex align-center mb-1">
                      <span v-for="n in 5" :key="n" class="star mr-1" :class="{ 'filled': n <= review.rating }">★</span>
                      <span class="body-2 ml-2">{{ review.comment || review.text }}</span>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <div v-for="review in recentReviews" :key="review.id" class="mb-3">
                    <div class="d-flex align-center mb-1">
                      <span v-for="n in 5" :key="n" class="star mr-1" :class="{ 'filled': n <= review.rating }">★</span>
                      <span class="body-2 ml-2">{{ review.text }}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <v-btn
                    small
                    color="primary"
                    outlined
                    @click="viewAllReviews"
                    class="text-transform-none"
                  >
                    View All
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Quick Actions -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <span class="material-icons-round mr-2">flash_on</span>
                Quick Actions
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="primary"
                      class="mb-2"
                      :disabled="dealerStatus !== '1'"
                    >
                      <span class="material-icons-round mr-2">add</span>
                      Add Vehicle
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="success"
                      class="mb-2"
                      :disabled="dealerStatus !== '1'"
                    >
                      <span class="material-icons-round mr-2">group</span>
                      View Referrals
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      color="info"
                      class="mb-2"
                      :disabled="dealerStatus !== '1'"
                    >
                      <span class="material-icons-round mr-2">analytics</span>
                      View Reports
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <v-btn
                      block
                      large
                      dark
                      color="orange"
                      class="mb-2"
                    >
                      <span class="material-icons-round mr-2">settings</span>
                      Settings
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Recent Activity -->
        <v-row>
          <v-col cols="12" md="8">
            <v-card>
              <v-card-title>
                <span class="material-icons-round mr-2">history</span>
                Recent Activity
              </v-card-title>
              <v-card-text>
                <v-list>
                  <v-list-item>
                    <v-list-item-icon>
                      <span class="material-icons-round">check_circle</span>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Account Created</v-list-item-title>
                      <v-list-item-subtitle>Your dealer account was successfully created</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-list-item-action-text>Today</v-list-item-action-text>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card>
              <v-card-title>
                <span class="material-icons-round mr-2">support</span>
                Support
              </v-card-title>
              <v-card-text>
                <p class="body-2 mb-3">Need help getting started?</p>
                <v-btn dark color="blue" block class="mb-2">
                  <span class="material-icons-round mr-2">help</span>
                  Help Center
                </v-btn>
                <v-btn dark color="green" block>
                  <span class="material-icons-round mr-2">chat</span>
                  Contact Support
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Footer -->
    <FooterDealer class="with-sidebar" />
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerDashboard',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      userName: '',
      businessName: '',
      dealerStatus: '0',
      inventoryCount: '0',
      dashboardData: {
        stats: {
          live_vehicles: 0,
          active_agents: 0,
          pending_leads: 0,
          unpaid_commissions: '0.00'
        },
        recent_activity: [],
        agent_performance: [],
        commission_overview: {
          current_month: '0.00',
          last_month: '0.00',
          total_earned: '0.00',
          pending_payout: '0.00'
        },
        recent_reviews: []
      },
      loading: true,
      lastSyncTime: '2 hrs ago',
      commissionData: {
        flatRate: 500,
        tieredRate: 3,
        tieredThreshold: 25
      },
      agentPerformance: {
        topAgent: {
          name: 'David J.',
          leads: 12
        },
        avgResponse: 1.4,
        activeAgents: 62
      },
      recentReviews: [
        { id: 1, rating: 5, text: 'Smooth process!' },
        { id: 2, rating: 4, text: 'Agent was helpful' },
        { id: 3, rating: 5, text: 'Fast payout' }
      ],
      // Dynamic data from API - initialized with loading values
      inventoryStats: {
        live_vehicles: 0,
        pending_approval: 0,
        last_sync: 'Loading...'
      },
      commissionOverview: {
        flat_rate: 0,
        tiered_rate: 0,
        tiered_threshold: 0
      },
      agentPerformanceData: {
        top_agent_name: 'Loading...',
        top_agent_leads: 0,
        avg_response_time: 0,
        active_agents: 0
      }
    }
  },
  beforeCreate() {
    // Check if user is logged in as dealer
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  mounted() {
    this.loadUserData()
  },
  methods: {
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Your Business'
      this.dealerStatus = localStorage.getItem('dealer_status') || '0'
      // TODO: Load actual inventory count from API
      this.loadDashboardData()
    },

    async loadDashboardData() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')

        if (!token) {
          this.$router.push('/dealers/login')
          return
        }

        const response = await axios.get('/api/dealer/dashboard-data', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.flag === 1) {
          const data = response.data.data

          // Update dealer info
          this.userName = data.dealer_info.name
          this.businessName = data.dealer_info.business_name
          this.dealerStatus = data.dealer_info.dealer_status

          // Update dashboard data
          this.dashboardData = {
            stats: data.stats,
            recent_activity: data.recent_activity,
            agent_performance: data.agent_performance,
            commission_overview: data.commission_overview,
            recent_reviews: data.recent_reviews
          }

          // Update dynamic card data
          if (data.inventory_stats) {
            this.inventoryStats = {
              live_vehicles: data.inventory_stats.live_vehicles,
              pending_approval: data.inventory_stats.pending_approval,
              last_sync: data.inventory_stats.last_sync
            }
          }

          if (data.commission_data) {
            this.commissionOverview = {
              flat_rate: data.commission_data.flat_rate,
              tiered_rate: data.commission_data.tiered_rate,
              tiered_threshold: data.commission_data.tiered_threshold
            }
          }

          if (data.agent_performance_data) {
            this.agentPerformanceData = {
              top_agent_name: data.agent_performance_data.top_agent_name,
              top_agent_leads: data.agent_performance_data.top_agent_leads,
              avg_response_time: data.agent_performance_data.avg_response_time,
              active_agents: data.agent_performance_data.active_agents
            }
          }

          console.log('Dashboard data loaded successfully')
        } else {
          this.$toast.error('Failed to load dashboard data', {
            position: 'top-right'
          })
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error)
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token')
          this.$router.push('/dealers/login')
        } else {
          this.$toast.error('Error loading dashboard data', {
            position: 'top-right'
          })
        }
      } finally {
        this.loading = false
      }
    },

    handleNavigation(item) {
      // Handle navigation from header sidebar
      console.log('Navigating to:', item)
      // TODO: Implement navigation logic for different sections
    },

    setActiveNavItem(item) {
      this.activeNavItem = item
      // TODO: Implement navigation logic for different sections
      console.log('Navigating to:', item)
    },

    manageInventory() {
      this.setActiveNavItem('Inventory')
      // TODO: Navigate to inventory management page
      console.log('Navigate to inventory management')
    },

    editCommissions() {
      this.setActiveNavItem('Commission')
      // TODO: Navigate to commission settings
      console.log('Navigate to commission settings')
    },

    viewAgents() {
      this.setActiveNavItem('Referring Agents')
      // TODO: Navigate to agents page
      console.log('Navigate to referring agents')
    },

    viewAllReviews() {
      this.setActiveNavItem('Reviews')
      // TODO: Navigate to reviews page
      console.log('Navigate to all reviews')
    }
  }
}
</script>

<style scoped>






/* Main Content */
.dashboard-main {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: calc(100vh - 160px);
}

.dashboard-container {
  padding: 40px 20px;
}

.welcome-card {
  border-radius: 20px !important;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.3) !important;
}

.welcome-icon {
  opacity: 0.2;
}

.stat-card {
  border-radius: 16px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.9) !important;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
}

/* Star Rating Styles */
.star {
  color: #ddd;
  font-size: 16px;
}

.star.filled {
  color: #ffc107;
}

/* Material Icons styling */
.material-icons-round {
  font-family: 'Material Icons Round';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Responsive Design */
@media (max-width: 960px) {
  .logo-text {
    display: none;
  }

  .user-info {
    display: none !important;
  }
}

@media (max-width: 600px) {
  .dashboard-container {
    padding: 20px 10px;
  }

  .dealer-header .v-container {
    padding: 0 16px;
  }
}
</style>
